"use client";

import {
  addImmatriculationCodeRejetRecyclAuto,
  editImmatriculationCodeRejetRecyclAuto,
  deleteImmatriculationCodeRejetRecyclAuto,
} from "@/app/lib/actions/immat-code-rejet-recycl-auto";

import {
  addImmatriculationCodeRejetRecyclAutoSchema,
  editImmatriculationCodeRejetRecyclAutoSchema,
} from "@/lib/schemas/immat-code-rejet-recycl-auto";

import {
  useImmatriculationCodeRejetRecyclAutoQuery,
  invalidateImmatriculationCodeRejetRecyclAutoQuery,
} from "@/lib/queries/immat-code-rejet-recycl-auto";

import { ItemContent, ItemTitle } from "@/components/ui/item";
import {
  ItemsListCard,
  ItemsListCardSkeleton,
} from "@/components/backoffice/items-list-card";

import {
  Field,
  FieldError,
  FieldGroup,
  FieldLabel,
} from "@/components/ui/field";

import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import * as z from "zod";
import { formatDisplayDate } from "@/lib/utils";

type ImmatriculationCodeRejetRecyclAuto = {
  id: number;
  operationType: string;
  rejectCode: string;
  startDate: Date;
  endDate: Date | null;
  isActive: boolean;
};

const OPERATION_TYPE_OPTIONS = [
  { label: "DCT", value: "DCT" },
  { label: "DCL", value: "DCL" },
  { label: "DVD", value: "DVD" },
];

function ImmatriculationCodeRejetRecyclAutoItemsListCard() {
  const queryClient = useQueryClient();

  const query = useImmatriculationCodeRejetRecyclAutoQuery({
    sort: { operationType: "asc" },
  });

  const handleCreate = async (
    data: z.infer<typeof addImmatriculationCodeRejetRecyclAutoSchema>,
  ) => {
    const response = await addImmatriculationCodeRejetRecyclAuto(data);

    if (!response.success) {
      toast.error(response.message);
      return;
    }

    toast.success("Paramètre créé.");

    invalidateImmatriculationCodeRejetRecyclAutoQuery(queryClient);
  };

  const handleUpdate = async (
    data: z.infer<typeof editImmatriculationCodeRejetRecyclAutoSchema>,
  ) => {
    const response = await editImmatriculationCodeRejetRecyclAuto(data);

    if (!response.success) {
      toast.error(response.message);
      return;
    }

    toast.success("Paramètre modifié.");

    invalidateImmatriculationCodeRejetRecyclAutoQuery(queryClient);
  };

  const handleDelete = async (id: { id: number }) => {
    const response = await deleteImmatriculationCodeRejetRecyclAuto(id);

    if (!response.success) {
      toast.error(response.message);
      return;
    }

    toast.success("Paramètre supprimé.");

    invalidateImmatriculationCodeRejetRecyclAutoQuery(queryClient);
  };

  if (query.isPending) {
    return <ItemsListCardSkeleton />;
  }

  const items = query.data?.immatriculationCodeRejetRecyclAuto ?? [];

  return (
    <ItemsListCard<ImmatriculationCodeRejetRecyclAuto>
      title="SIV - Paramétrage codes rejets techniques"
      items={items}
      createSchema={addImmatriculationCodeRejetRecyclAutoSchema}
      editSchema={editImmatriculationCodeRejetRecyclAutoSchema}
      defaultCreateValues={{
        operationType: "",
        rejectCode: "",
        startDate: new Date(),
        endDate: null,
        isActive: true,
      }}
      onCreate={handleCreate}
      onUpdate={handleUpdate}
      onDelete={handleDelete}
      renderItem={(item) => (
        <ItemContent>
          <ItemTitle>
            {item.operationType} - {item.rejectCode}
          </ItemTitle>

          <div className="flex flex-wrap items-center gap-3 text-sm text-slate-700">
            <span>
              {"Type d'opèration"}:{" "}
              <span className="font-mono">{item.operationType}</span>
            </span>

            <span className="text-slate-300">|</span>

            <span>
              Code: <span className="font-mono">{item.rejectCode}</span>
            </span>

            <span className="text-slate-300">|</span>

            <span>
              Date de début:{" "}
              <span className="font-mono">
                {formatDisplayDate(item.startDate)}
              </span>
            </span>

            <span className="text-slate-300">|</span>

            <span>
              Date de fin:{" "}
              <span className="font-mono">
                {item.endDate ? formatDisplayDate(item.endDate) : "-"}
              </span>
            </span>
          </div>
        </ItemContent>
      )}
      renderFormFields={(form) => (
        <FieldGroup>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <form.Field name="operationType">
              {(field) => (
                <Field>
                  <FieldLabel>{"Type d'opération"}</FieldLabel>

                  <Select
                    value={field.state.value}
                    onValueChange={field.handleChange}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionner" />
                    </SelectTrigger>

                    <SelectContent>
                      {OPERATION_TYPE_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <FieldError errors={field.state.meta.errors} />
                </Field>
              )}
            </form.Field>

            <form.Field name="rejectCode">
              {(field) => (
                <Field>
                  <FieldLabel>Code rejet</FieldLabel>

                  <Input
                    maxLength={3}
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                  />

                  <FieldError errors={field.state.meta.errors} />
                </Field>
              )}
            </form.Field>

            <form.Field name="startDate">
              {(field) => (
                <Field>
                  <FieldLabel>Date début</FieldLabel>

                  <Input
                    type="date"
                    value={
                      field.state.value
                        ? new Date(field.state.value)
                            .toISOString()
                            .split("T")[0]
                        : ""
                    }
                    onChange={(e) =>
                      field.handleChange(
                        e.target.value ? new Date(e.target.value) : new Date(),
                      )
                    }
                  />

                  <FieldError errors={field.state.meta.errors} />
                </Field>
              )}
            </form.Field>

            <form.Field name="endDate">
              {(field) => (
                <Field>
                  <FieldLabel>Date fin</FieldLabel>

                  <Input
                    type="date"
                    value={
                      field.state.value
                        ? new Date(field.state.value)
                            .toISOString()
                            .split("T")[0]
                        : ""
                    }
                    onChange={(e) =>
                      field.handleChange(
                        e.target.value ? new Date(e.target.value) : null,
                      )
                    }
                  />

                  <FieldError errors={field.state.meta.errors} />
                </Field>
              )}
            </form.Field>
          </div>

          <form.Field name="isActive">
            {(field) => (
              <Field>
                <FieldLabel htmlFor={field.name}>Statut</FieldLabel>
                <Switch
                  id="switch"
                  checked={field.state.value}
                  onCheckedChange={field.handleChange}
                />
              </Field>
            )}
          </form.Field>
        </FieldGroup>
      )}
      pageSize={10}
    />
  );
}

export { ImmatriculationCodeRejetRecyclAutoItemsListCard };
